import { useState, useEffect } from "react";
import axios from "axios";
import {
  Grid,
  Card,
  Button,
  TextField,
  Snackbar,
  Alert,
  Typography,
  IconButton,
  Divider,
  Box,
} from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Add, Delete, Edit, List } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayoutMin from "examples/LayoutContainers/DashboardLayoutMin";

function ExamPaperTemplate() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [viewMode, setViewMode] = useState("list"); // list | edit
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [examMarks, setExamMarks] = useState("");
  const [examDuration, setExamDuration] = useState("");
  const [pages, setPages] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  // 🔄 Load all templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/list`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` } }
      );
      setTemplates(res.data.data || []);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load templates", "error");
    } finally {
      setLoading(false);
    }
  };

  // 📄 Load one template for editing
  const handleEditTemplate = async (templateId) => {
    try {
      const checkRes = await axios.get(
        `${API_BASE_URL}/api/exam-template/${clientId}/check-mapped/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      if (checkRes.data.isMapped) {
        showSnackbar("Template is already mapped to a course and cannot be edited", "error");
        return;
      }

      // Existing edit code
      const res = await axios.get(
        `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      const data = res.data.data;

      setSections(data.sections || []);
      setTemplateName(data.template_name || "");
      setExamMarks(data.exam_marks || "");
      setExamDuration(data.exam_duration || "");
      setPages(data.pages || "");
      setSelectedTemplate(templateId);
      setViewMode("edit");
    } catch (err) {
      showSnackbar("Failed to load template", "error");
    }
  };

  // ❌ Delete template
  const handleDeleteTemplate = async (templateId) => {
    try {
      const checkRes = await axios.get(
        `${API_BASE_URL}/api/exam-template/${clientId}/check-mapped/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      if (checkRes.data.isMapped) {
        showSnackbar("Template is already mapped to a course and cannot be deleted", "error");
        return;
      }

      if (!window.confirm("Are you sure you want to delete this template?")) return;

      await axios.delete(
        `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/delete/${templateId}`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      showSnackbar("Template deleted", "success");
      fetchTemplates();
    } catch (err) {
      showSnackbar("Failed to delete template", "error");
    }
  };

  useEffect(() => {
    if (viewMode === "list") fetchTemplates();
  }, [viewMode]);

  // ➕ Add section
  const handleAddSection = () => {
    setSections((prev) => [
      ...prev,
      {
        section_label: String.fromCharCode(65 + prev.length),
        section_title: "",
        attempt_rule: "ALL",
        attempt_count: null,
        total_questions: 1,
        marks_per_question: 0,
        section_instructions: "",
        section_order: prev.length + 1,
        questions: [
          {
            question_number: 1,
            sub_label: "",
            question_text: "",
            marks: 0,
            is_choice_group: 0,
            choice_group_label: "",
          },
        ],
      },
    ]);
  };
  // 🧩 Section + Question handlers
  const handleDeleteSection = (i) => setSections(sections.filter((_, x) => x !== i));
  const handleAddQuestion = (sec) => {
    const updated = [...sections];
    updated[sec].questions.push({
      question_number: updated[sec].questions.length + 1,
      sub_label: "",
      question_text: "",
      marks: updated[sec].marks_per_question || 0,
      is_choice_group: 0,
      choice_group_label: "",
    });
    setSections(updated);
  };
  const handleDeleteQuestion = (sec, q) => {
    const updated = [...sections];
    updated[sec].questions.splice(q, 1);
    setSections(updated);
  };
  const handleSectionChange = (sec, field, val) => {
    const updated = [...sections];
    updated[sec][field] = val;
    if (field === "total_questions") {
      const total = parseInt(val) || 0;
      const marks = updated[sec].marks_per_question || 0;
      const existing = updated[sec].questions.length;
      if (total > existing) {
        for (let i = existing; i < total; i++) {
          updated[sec].questions.push({
            question_number: i + 1,
            sub_label: "",
            question_text: "",
            marks,
            is_choice_group: 0,
            choice_group_label: "",
          });
        }
      } else if (total < existing) {
        updated[sec].questions = updated[sec].questions.slice(0, total);
      }
    }
    setSections(updated);
  };
  const handleQuestionChange = (sec, q, field, val) => {
    const updated = [...sections];
    updated[sec].questions[q][field] = val;
    setSections(updated);
  };

  // 💾 Save template (new or update)
  const handleSaveTemplate = async () => {
    try {
      const payload = {
        college_id: collegeId,
        sections,

        college_id: collegeId,
        exam_template_id: selectedTemplate,
        template_name: templateName,
        exam_marks: parseInt(examMarks) || 0,
        exam_duration: parseInt(examDuration) || 0,
        pages: parseInt(pages) || 0,
      };
      const url = selectedTemplate
        ? `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/update/${selectedTemplate}`
        : `${API_BASE_URL}/api/exam-template/${clientId}/${collegeId}/save`;

      const method = selectedTemplate ? "put" : "post";

      await axios({
        method,
        url,
        data: payload,
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
      });

      showSnackbar(
        selectedTemplate ? "Template updated successfully!" : "Template saved successfully!",
        "success"
      );
      setTemplateName("");
      setExamMarks("");
      setExamDuration("");
      setPages("");
      setSections([]);
      setViewMode("list");
      fetchTemplates();
    } catch (err) {
      console.error(err);
      showSnackbar("Error saving template", "error");
    }
  };

  // 📋 Template List columns
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "template_name", headerName: "Template Name", flex: 1 },
    { field: "exam_marks", headerName: "Total Marks", width: 120 },
    { field: "exam_duration", headerName: "Duration (min)", width: 140 },
    { field: "pages", headerName: "Pages", width: 100 },
    { field: "created_on", headerName: "Created On", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleEditTemplate(params.row.exam_template_id)}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteTemplate(params.row.exam_template_id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <DashboardLayoutMin>
      <MDBox
        sx={{
          height: "calc(100vh - 80px)",
          overflowY: "auto",
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
        }}
      >
        {/* ====== LIST VIEW ====== */}
        {viewMode === "list" && (
          <Card sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Exam Templates</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setSections([]);
                  setSelectedTemplate(null);
                  setViewMode("edit");
                }}
                sx={{
                  backgroundColor: "#2f00ff",
                  color: "#fff",
                  fontSize: "0.75rem",
                  px: 1,
                  py: 0.4,
                  minWidth: { xs: 70, sm: 100 },
                  "&:hover": { backgroundColor: "#2700d6" },
                }}
              >
                Add New Template
              </Button>
            </Box>
            <div style={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={templates.map((t, i) => ({ id: t.exam_template_id || i, ...t }))}
                columns={columns}
                pageSize={5}
                rowsPerPageOptions={[5, 10]}
                loading={loading}
              />
            </div>
          </Card>
        )}

        {/* ====== EDIT VIEW ====== */}
        {viewMode === "edit" && (
          <Card sx={{ p: { xs: 2, sm: 3 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {selectedTemplate ? "Edit Template" : "New Template"}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<List />}
                onClick={() => {
                  setViewMode("list");
                  setSections([]);
                  setSelectedTemplate(null);
                }}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1565c0" },
                }}
              >
                Back to List
              </Button>
            </Box>

            {/* Section builder (reusing your logic) */}
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="Template Name"
                    fullWidth
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Exam Marks"
                    type="number"
                    fullWidth
                    value={examMarks}
                    onChange={(e) => setExamMarks(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Exam Duration (Minutes)"
                    type="number"
                    fullWidth
                    value={examDuration}
                    onChange={(e) => setExamDuration(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField
                    label="Pages"
                    type="number"
                    fullWidth
                    value={pages}
                    onChange={(e) => setPages(e.target.value)}
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSection}
                size="medium"
                sx={{
                  backgroundColor: "#2f00ff",
                  color: "#fff",
                  fontSize: "0.75rem",
                  px: 1,
                  py: 0.4,
                  minWidth: { xs: 70, sm: 100 },
                  "&:hover": { backgroundColor: "#2700d6" },
                }}
              >
                Add Section
              </Button>

              {sections.map((section, secIndex) => (
                <Card key={secIndex} sx={{ p: 2, mb: 3, border: "1px solid #ccc" }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={11}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Section {section.section_label}
                      </Typography>
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton color="error" onClick={() => handleDeleteSection(secIndex)}>
                        <Delete />
                      </IconButton>
                    </Grid>

                    <Grid item xs={4}>
                      <TextField
                        label="Section Title"
                        fullWidth
                        value={section.section_title}
                        onChange={(e) =>
                          handleSectionChange(secIndex, "section_title", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        label="Attempt Rule"
                        select
                        fullWidth
                        SelectProps={{ native: true }}
                        value={section.attempt_rule}
                        onChange={(e) =>
                          handleSectionChange(secIndex, "attempt_rule", e.target.value)
                        }
                      >
                        <option value="ALL">ALL</option>
                        <option value="ANY_N">ANY N</option>
                      </TextField>
                    </Grid>
                    {section.attempt_rule === "ANY_N" && (
                      <Grid item xs={2}>
                        <TextField
                          label="Attempt Count (N)"
                          type="number"
                          fullWidth
                          value={section.attempt_count || ""}
                          onChange={(e) =>
                            handleSectionChange(secIndex, "attempt_count", e.target.value)
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={2}>
                      <TextField
                        label="Total Questions"
                        type="number"
                        fullWidth
                        value={section.total_questions}
                        onChange={(e) =>
                          handleSectionChange(secIndex, "total_questions", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        label="Marks per Question"
                        type="number"
                        fullWidth
                        value={section.marks_per_question}
                        onChange={(e) =>
                          handleSectionChange(secIndex, "marks_per_question", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Section Instructions"
                        fullWidth
                        multiline
                        rows={2}
                        value={section.section_instructions}
                        onChange={(e) =>
                          handleSectionChange(secIndex, "section_instructions", e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Questions</Typography>
                    <Button
                      variant="outlined"
                      size="medium"
                      startIcon={<Add />}
                      onClick={() => handleAddQuestion(secIndex)}
                      sx={{
                        backgroundColor: "#2f00ff",
                        color: "#fff",
                        fontSize: "0.75rem",
                        px: 1,
                        py: 0.4,
                        minWidth: { xs: 70, sm: 100 },
                        "&:hover": { backgroundColor: "#2700d6" },
                      }}
                    >
                      Add Question
                    </Button>
                  </Box>

                  {section.questions.map((q, qIndex) => (
                    <Grid container spacing={2} key={qIndex} sx={{ mt: 1 }}>
                      <Grid item xs={1}>
                        <TextField
                          label="#"
                          type="number"
                          value={q.question_number}
                          onChange={(e) =>
                            handleQuestionChange(
                              secIndex,
                              qIndex,
                              "question_number",
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          label="Sub (a/b)"
                          value={q.sub_label}
                          onChange={(e) =>
                            handleQuestionChange(secIndex, qIndex, "sub_label", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          label="Question Text"
                          fullWidth
                          multiline
                          value={q.question_text}
                          onChange={(e) =>
                            handleQuestionChange(secIndex, qIndex, "question_text", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          label="Marks"
                          type="number"
                          value={q.marks}
                          onChange={(e) =>
                            handleQuestionChange(secIndex, qIndex, "marks", e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <TextField
                          label="Choice Group"
                          value={q.choice_group_label || ""}
                          onChange={(e) =>
                            handleQuestionChange(
                              secIndex,
                              qIndex,
                              "choice_group_label",
                              e.target.value
                            )
                          }
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteQuestion(secIndex, qIndex)}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Card>
              ))}

              {sections.length > 0 && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPreviewOpen(true)}
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#1565c0" },
                    }}
                  >
                    Preview & Save
                  </Button>
                </Box>
              )}
            </Box>
          </Card>
        )}
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            backgroundColor: snackbar.severity === "error" ? "#d32f2f" : undefined,
            color: "#fff",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* ===== Preview Dialog ===== */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Preview Exam Template</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Typography variant="h6" gutterBottom>
            {templateName}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Marks: {examMarks} | Duration: {examDuration} minutes | Pages: {pages}
          </Typography>
          <Divider sx={{ my: 1 }} />

          {sections.map((section, secIndex) => (
            <Box key={secIndex} mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Section {section.section_label}: {section.section_title}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Attempt Rule: {section.attempt_rule}
                {section.attempt_rule === "ANY_N" && ` (${section.attempt_count})`}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Questions: {section.total_questions} | Marks/Q: {section.marks_per_question}
              </Typography>
              {section.section_instructions && (
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  {section.section_instructions}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              {section.questions.map((q, qIndex) => (
                <Box key={qIndex} sx={{ mb: 1, pl: 2 }}>
                  <Typography variant="body2">
                    {q.question_number}. {q.sub_label && `(${q.sub_label})`}{" "}
                    {q.question_text || <i>[No text]</i>} — <b>{q.marks} Marks</b>
                    {q.choice_group_label && (
                      <Typography component="span" sx={{ color: "text.secondary" }}>
                        {" "}
                        | Group: {q.choice_group_label}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewOpen(false)}
            color="error"
            sx={{
              backgroundColor: "#e9863aff",
              color: "#fff",
              "&:hover": { backgroundColor: "#e16442ff" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setPreviewOpen(false);
              handleSaveTemplate();
            }}
            variant="contained"
            color="success"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayoutMin>
  );
}

export default ExamPaperTemplate;
